import LegalLayout from "@/components/LegalLayout";
import useSiteContent from "@/hooks/useSiteContent";

const TOC = [
    { id: "acceptance", label: "1. Acceptance" },
    { id: "service", label: "2. Service description" },
    { id: "eligibility", label: "3. Eligibility" },
    { id: "responsibilities", label: "4. Doctor responsibilities" },
    { id: "payment", label: "5. Payment & AMC" },
    { id: "ip", label: "6. Intellectual property" },
    { id: "termination", label: "7. Suspension & termination" },
    { id: "disclaimer", label: "8. Disclaimer & liability" },
    { id: "law", label: "9. Governing law" },
    { id: "changes", label: "10. Changes to terms" },
    { id: "contact", label: "11. Contact" },
];

export default function Terms() {
    const { content } = useSiteContent();
    // If super admin has supplied a `terms_html` override, render THAT
    // wholesale inside LegalLayout — the default TOC still renders. Otherwise
    // fall back to the hard-coded body below.
    if (content.terms_html && content.terms_html.trim()) {
        return (
            <LegalLayout title="Terms &amp; Conditions" updated="Live · CMS" toc={TOC}>
                <div data-testid="terms-cms-body" dangerouslySetInnerHTML={{ __html: content.terms_html }} />
            </LegalLayout>
        );
    }
    return (
        <LegalLayout title="Terms &amp; Conditions" updated="February 2026" toc={TOC}>
            <p className="text-sm text-[#17212B]/70">
                These are the standard terms of service that govern your use of
                the Doctor Digital Identity Platform. By registering, paying, or
                using any feature you agree to be bound by them. Please read
                them carefully. Standard boilerplate — subject to refinement by
                counsel prior to public launch.
            </p>

            <h2 id="acceptance">1. Acceptance of terms</h2>
            <p>
                These Terms &amp; Conditions (&ldquo;Terms&rdquo;) form a binding
                agreement between you (&ldquo;Doctor&rdquo;, &ldquo;User&rdquo;)
                and <strong>HUMANITYFIRST SOFTWARE TECHNOLOGY PRIVATE LIMITED</strong>
                {" "}(&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;),
                operator of the Doctor Digital Identity Platform (the
                &ldquo;Service&rdquo;). By creating an account or paying for a
                package you accept these Terms and our <a href="/privacy">Privacy Policy</a>
                {" "}and <a href="/refund">Refund Policy</a>.
            </p>

            <h2 id="service">2. Service description</h2>
            <p>
                The Service is a professional identity and brand platform for
                medical practitioners. It composes and publishes a public
                digital identity website containing the Doctor&rsquo;s
                credentials, biography, consultation information, and related
                content. The Service is a <strong>marketing / branding
                platform</strong>; it is <strong>NOT a medical diagnostic,
                treatment, prescription, telemedicine, or emergency service</strong>.
                All medical decisions remain solely between the Doctor and the
                patient.
            </p>

            <h2 id="eligibility">3. Eligibility</h2>
            <ul>
                <li>You must hold a valid medical registration issued by a recognised medical council in India.</li>
                <li>You must be at least 18 years of age and legally competent to enter into contracts.</li>
                <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
            </ul>

            <h2 id="responsibilities">4. Doctor responsibilities</h2>
            <ul>
                <li><strong>Accurate credentials</strong>: you warrant that all information you submit (name, registration number, qualifications, awards, publications, memberships, affiliations) is true, current, and lawfully yours to publish.</li>
                <li><strong>No misleading claims</strong>: you will not publish diagnostic guarantees, cure claims, superlatives, or any content that violates the Indian Medical Council (Professional Conduct, Etiquette and Ethics) Regulations or applicable advertising laws.</li>
                <li><strong>Photos &amp; documents</strong>: you have the rights to any photos, logos, or documents you upload.</li>
                <li><strong>Compliance with local law</strong>: your use of the Service must comply with all laws applicable to your medical practice.</li>
            </ul>

            <h2 id="payment">5. Payment and AMC</h2>
            <ul>
                <li>Packages are billed in <strong>two 50% installments</strong>: an advance at signup and a balance before publication. Prices are shown per-package on the <a href="/pricing">Pricing</a> page in Indian Rupees (INR), inclusive of applicable GST unless otherwise stated.</li>
                <li>Payment is processed via a licensed Indian payment aggregator. We do not store your card / UPI credentials on our servers.</li>
                <li>An <strong>Annual Maintenance Charge (AMC)</strong> applies after the first year of publication (rate varies by package, listed on the Pricing page). AMC covers hosting, security, and content updates.</li>
                <li>Refunds are governed by our <a href="/refund">Refund &amp; Cancellation Policy</a>.</li>
            </ul>

            <h2 id="ip">6. Intellectual property</h2>
            <p>
                You retain ownership of the content you upload (photos,
                credentials, biography notes). You grant us a limited,
                worldwide, royalty-free licence to host, display, and
                distribute that content solely to operate the Service and
                render your public identity page. The platform software,
                design, brand marks, and AI outputs (as processed by our
                pipeline) remain the property of the Company.
            </p>

            <h2 id="termination">7. Suspension &amp; termination</h2>
            <p>
                We may suspend or terminate an account on notice if you
                (a) breach these Terms, (b) publish misleading or unlawful
                content, (c) misuse the Service or disrupt other users, or
                (d) fail to complete payment. You may close your account at
                any time via the in-app Settings; residual data handling is
                described in the <a href="/privacy">Privacy Policy</a>.
            </p>

            <h2 id="disclaimer">8. Disclaimer &amp; limitation of liability</h2>
            <p>
                The Service is provided on an &ldquo;as-is&rdquo; and
                &ldquo;as-available&rdquo; basis. To the maximum extent
                permitted by law, the Company&rsquo;s aggregate liability for
                any claim arising out of your use of the Service will not
                exceed the fees paid by you to the Company in the twelve
                months preceding the event giving rise to the claim. The
                Company is not liable for indirect, consequential, or
                incidental damages. Nothing in these Terms limits liability
                for gross negligence, fraud, or death / personal injury
                caused by our negligence.
            </p>

            <h2 id="law">9. Governing law &amp; jurisdiction</h2>
            <p>
                These Terms are governed by the laws of India. The courts of
                Mumbai, Maharashtra shall have exclusive jurisdiction over
                any dispute arising in connection with these Terms or the
                Service, subject to any statutory right you may have to
                approach a consumer forum.
            </p>

            <h2 id="changes">10. Changes to these terms</h2>
            <p>
                We may update these Terms from time to time. Material changes
                will be notified via email or an in-app banner at least 7
                days before they take effect. Continued use of the Service
                after the effective date constitutes acceptance of the
                updated Terms.
            </p>

            <h2 id="contact">11. Contact</h2>
            <p>
                Questions about these Terms? See <a href="/contact">/contact</a>
                {" "}or call <a href="tel:+917722090290">+91 7722090290</a>.
                Registered address: HUMANITYFIRST SOFTWARE TECHNOLOGY PRIVATE
                LIMITED, A-803 Palatial Height, Chandivali Farm Road,
                Andheri, Mumbai, Maharashtra 400072, India.
            </p>
        </LegalLayout>
    );
}
